import { ArrowLeft } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import type { GameInfo } from '../lib/games'
import { BottomNav, joinTab, menuTab, type NavTab, sheetTab } from './BottomNav'
import { BottomSheet } from './BottomSheet'
import { JoinCodeForm } from './JoinCodeForm'
import { JoinSheet } from './JoinSheet'
import { MenuSheet } from './MenuSheet'
import { Card } from './ui'

type Sheet = 'create' | 'join' | 'menu' | null

interface Props {
  current: GameInfo['to']
  roomPath: '/ice/r' | '/cycles/r'
  /** The "create a room" body, rendered once in the desktop card and once in the phone sheet. */
  create: { title: string; icon: ReactNode; tabLabel: string; panel: ReactNode }
  join: { id: string; label: string; title: string }
  /** The first bottom tab (Solo levels, Quick play…); the sheet is passed so it can open one. */
  firstTab: (openCreate: () => void) => NavTab
  /** Game-specific body under the create/join cards (levels, rules…). */
  children: ReactNode
}

/**
 * The home page of a party game: back link, create + join cards on desktop, the game's own body,
 * and the phone bottom bar whose Create / Join / Menu tabs open sheets.
 */
export function PartyHome({ current, roomPath, create, join, firstTab, children }: Props) {
  const navigate = useNavigate()
  const [sheet, setSheet] = useState<Sheet>(null)
  const closeSheet = () => setSheet(null)

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-5 py-4">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
      >
        <ArrowLeft className="size-4" aria-hidden /> All games
      </Link>

      {/* Phones reach these through the tabs below. */}
      <section aria-labelledby="party-create" className="hidden gap-3 lg:grid lg:grid-cols-2">
        <Card className="flex flex-col gap-3">
          <h2 id="party-create" className="font-semibold">
            {create.icon} {create.title}
          </h2>
          {create.panel}
        </Card>
        <Card>
          <JoinCodeForm
            id={join.id}
            label={join.label}
            onJoin={(code) => navigate(`${roomPath}/${code}`)}
          />
        </Card>
      </section>

      {children}

      <BottomNav
        tabs={[
          firstTab(() => setSheet('create')),
          sheetTab('create', create.tabLabel, create.icon, sheet, setSheet),
          joinTab(sheet, setSheet),
          menuTab(sheet, setSheet),
        ]}
      />
      <BottomSheet open={sheet === 'create'} onClose={closeSheet} title={create.title}>
        {create.panel}
      </BottomSheet>
      <JoinSheet
        open={sheet === 'join'}
        onClose={closeSheet}
        id={`${join.id}-sheet`}
        path={roomPath}
        title={join.title}
        label={join.label}
      />
      <MenuSheet open={sheet === 'menu'} onClose={closeSheet} current={current} />
    </div>
  )
}
